import { useState } from "react";

const statusStyles = {
  queued: "bg-yellow-100 text-yellow-800",
  processing: "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800",
  expired: "bg-gray-100 text-gray-700",
};

function displayDate(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString();
}

export default function RecentReports({
  reports,
  loading,
  error,
  onDownload,
  onRetry,
}) {
  const [expandedReportIds, setExpandedReportIds] = useState(() => new Set());

  const toggleReport = (reportId) => {
    setExpandedReportIds((current) => {
      const next = new Set(current);
      if (next.has(reportId)) next.delete(reportId);
      else next.add(reportId);
      return next;
    });
  };

  return (
    <section
      className="mt-6 px-4 sm:px-6 lg:px-8"
      aria-labelledby="recent-reports-heading"
    >
      <div className="card-custom px-4 py-5 sm:px-6">
        <div className="mb-4">
          <h2
            id="recent-reports-heading"
            className="text-base font-semibold text-gray-900"
          >
            Recent reports
          </h2>
          <p className="text-sm text-gray-500">
            Reports and their selected parameters remain available for 24 hours.
          </p>
        </div>

        {loading ? (
          <p className="text-sm text-gray-500" role="status">
            Loading recent reports…
          </p>
        ) : error ? (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        ) : reports.length === 0 ? (
          <p className="text-sm text-gray-500">
            No reports have been requested in the last 24 hours.
          </p>
        ) : (
          <div className="space-y-3">
            {reports.map((report) => {
              const isExpanded = expandedReportIds.has(report.id);
              const detailsId = `report-details-${report.id}`;

              return (
                <div
                  key={report.id}
                  className="rounded-md border border-gray-200 bg-white p-4"
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <button
                      type="button"
                      className="min-w-0 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                      aria-expanded={isExpanded}
                      aria-controls={detailsId}
                      onClick={() => toggleReport(report.id)}
                    >
                      <p className="font-semibold text-gray-900">
                        {report.report_name}
                      </p>
                      <p className="text-xs text-gray-500">
                        Requested {displayDate(report.requested_at)}
                      </p>
                      <span className="mt-1 block text-xs font-medium text-indigo-600">
                        {isExpanded ? "Hide details" : "View details"}
                      </span>
                    </button>

                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`w-fit rounded-full px-2.5 py-1 text-xs font-semibold ${
                          statusStyles[report.status] ||
                          "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {report.status}
                      </span>
                      {report.download_url && (
                        <button
                          type="button"
                          className="min-h-[44px] rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white"
                          onClick={() => onDownload(report)}
                        >
                          Download
                        </button>
                      )}
                    </div>
                  </div>

                  {isExpanded && (
                    <div
                      id={detailsId}
                      className="mt-4 border-t border-gray-100 pt-3"
                    >
                      <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
                        {Object.entries(report.parameter_labels || {}).map(
                          ([label, value]) => (
                            <div key={label}>
                              <dt className="text-xs font-medium text-gray-500">
                                {label}
                              </dt>
                              <dd className="text-sm text-gray-900">
                                {String(value)}
                              </dd>
                            </div>
                          )
                        )}
                        <div>
                          <dt className="text-xs font-medium text-gray-500">
                            Expires
                          </dt>
                          <dd className="text-sm text-gray-900">
                            {displayDate(report.expires_at)}
                          </dd>
                        </div>
                      </dl>

                      {report.progress_message && (
                        <p className="mt-3 text-sm text-gray-600">
                          {report.progress_message}
                        </p>
                      )}
                      {report.error_message && (
                        <p className="mt-2 text-sm text-red-600">
                          {report.error_message}
                        </p>
                      )}

                      {report.can_retry && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          <button
                            type="button"
                            className="min-h-[44px] rounded-md border border-main-purple px-3 py-2 text-sm font-semibold main-purple"
                            onClick={() => onRetry(report)}
                          >
                            Generate again
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
